/**
 * Portfolio Health — Analyze portfolio risk & performance
 */
import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, Button, CircularProgress, TextField, IconButton,
  Skeleton, alpha, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, Tooltip,
} from '@mui/material';
import {
  HealthAndSafety, Add, Delete, Assessment,
  CheckCircle, Warning, Error as ErrorIcon, WarningAmber,
  Info, RemoveCircleOutline, HighlightOff, Visibility,
  Stars, ThumbUp, Report, AutoAwesome, ReportProblem,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { portfolioAPI, stockStrengthAPI } from '../services/api';

// ── Helpers ──
const thSx = { fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', letterSpacing: '0.02em', py: 0.8, borderBottom: '1px solid #e2e8f0', bgcolor: '#fafbfc' };
const tdSx = { py: 0.7, fontSize: '0.8rem', borderBottom: '1px solid #f1f5f9' };

// Strip ✅/❌ emojis from backend reason strings → render inline with MUI icons
const renderReason = (text) => {
  if (!text || typeof text !== 'string') return text;
  // Split by emoji markers while keeping them
  const parts = text.split(/(✅|❌|⚠️|🔴|🟡|🟢)/);
  return parts.map((p, i) => {
    if (p === '✅') return <CheckCircle key={i} sx={{ fontSize: 12, color: '#10b981', mx: 0.2, verticalAlign: 'text-bottom' }} />;
    if (p === '❌') return <HighlightOff key={i} sx={{ fontSize: 12, color: '#ef4444', mx: 0.2, verticalAlign: 'text-bottom' }} />;
    if (p === '⚠️' || p === '🟡') return <WarningAmber key={i} sx={{ fontSize: 12, color: '#f59e0b', mx: 0.2, verticalAlign: 'text-bottom' }} />;
    if (p === '🔴') return <ReportProblem key={i} sx={{ fontSize: 12, color: '#ef4444', mx: 0.2, verticalAlign: 'text-bottom' }} />;
    if (p === '🟢') return <CheckCircle key={i} sx={{ fontSize: 12, color: '#10b981', mx: 0.2, verticalAlign: 'text-bottom' }} />;
    return <React.Fragment key={i}>{p}</React.Fragment>;
  });
};

const getGrade = (s) => {
  if (s >= 80) return { grade: 'A', label: 'Excellent', icon: Stars, color: '#10b981' };
  if (s >= 60) return { grade: 'B', label: 'Good', icon: ThumbUp, color: '#8bc34a' };
  if (s >= 40) return { grade: 'C', label: 'Fair', icon: Warning, color: '#f59e0b' };
  if (s >= 20) return { grade: 'D', label: 'Weak', icon: Report, color: '#ff9800' };
  return { grade: 'F', label: 'Critical', icon: ErrorIcon, color: '#ef4444' };
};

// ── Stat Tile ──
const Tile = ({ label, value, sub, color }) => (
  <Box sx={{ textAlign: 'center', px: 1.5 }}>
    <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#94a3b8', mb: 0.3 }}>
      {label}
    </Typography>
    {sub && <Typography sx={{ fontSize: '0.62rem', color: '#cbd5e1', mb: 0.3 }}>({sub})</Typography>}
    <Typography sx={{ fontSize: '1.15rem', fontWeight: 800, color, fontFamily: 'monospace' }}>{value}</Typography>
  </Box>
);

// ── Component Card ──
const ComponentCard = ({ component, label, componentKey, lang, t, healthScore }) => {
  if (!component) return null;
  const displayName = (typeof component.name === 'object' && component.name[lang]) ? component.name[lang] : label;
  const norm = (component.score / 20) * 100;
  let adj = norm;
  if (componentKey === 'component_a' && healthScore?.portfolio_return_cumulative < 0) adj = Math.min(norm, 59);
  const g = getGrade(adj);
  const level = norm >= 60 ? 'good' : norm >= 40 ? 'fair' : 'poor';
  const keyMap = { component_a: 'performance', component_b: 'trend', component_c: 'liquidity', component_d: 'concentration', component_e: 'stress' };
  const explanation = t(`riskManagement.portfolioHealth.componentExplanations.${keyMap[componentKey]}.${level}`, '');

  // Sub-metrics
  const metricKeys = {
    excess_return_score: 'excessReturn', max_drawdown_score: 'riskControl',
    trend_score: 'trendScore', momentum_score: 'momentumScore',
    liquidity_score: 'liquidityScore', volume_confirmation_score: 'volumeConfirm',
    position_score: 'positionDiversity', sector_score: 'sectorDiversity',
    beta: 'beta', avg_gap: 'avgGap',
  };

  const fmtVal = (k, v) => {
    if (typeof v !== 'number') return v;
    if (k.includes('score')) return `${v.toFixed(1)}/10`;
    if (k.includes('return') || k.includes('drawdown') || k === 'avg_gap' || k.includes('position') || k.includes('sector')) return `${(v * 100).toFixed(1)}%`;
    return v.toFixed(2);
  };

  return (
    <Box sx={{
      borderRadius: 1.5, border: `1px solid ${alpha(g.color, 0.25)}`, bgcolor: '#fff', p: 1.25,
      display: 'flex', flexDirection: 'column', height: '100%',
      transition: 'all 0.2s', '&:hover': { borderColor: alpha(g.color, 0.5), boxShadow: `0 2px 10px ${alpha(g.color, 0.1)}` },
    }}>
      <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase', color: '#94a3b8', letterSpacing: '0.05em', mb: 0.5, lineHeight: 1.2, minHeight: 28 }}>
        {displayName}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.6, mb: 0.5 }}>
        <Typography sx={{ fontSize: '1.4rem', fontWeight: 900, color: g.color, lineHeight: 1, fontFamily: 'monospace' }}>
          {component.score?.toFixed(0) || '0'}
        </Typography>
        <Box sx={{ display: 'inline-flex', px: 0.6, py: 0.1, borderRadius: 0.6, bgcolor: alpha(g.color, 0.12) }}>
          <Typography sx={{ fontSize: '0.6rem', fontWeight: 800, color: g.color }}>{g.label}</Typography>
        </Box>
      </Box>
      {/* Score bar */}
      <Box sx={{ height: 3, borderRadius: 2, bgcolor: alpha(g.color, 0.1), mb: 0.8, overflow: 'hidden' }}>
        <Box sx={{ width: `${Math.min(adj, 100)}%`, height: '100%', bgcolor: g.color, transition: 'width .3s' }} />
      </Box>
      {explanation && (
        <Typography sx={{ fontSize: '0.68rem', color: '#64748b', lineHeight: 1.4, mb: 0.8, flex: 1 }}>{explanation}</Typography>
      )}
      {component.details && (
        <Box sx={{ pt: 0.5, borderTop: '1px dashed #e2e8f0', mt: 'auto' }}>
          {Object.entries(component.details).slice(0, 2).map(([k, v]) => {
            const mk = metricKeys[k];
            if (!mk) return null;
            return (
              <Box key={k} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.1 }}>
                <Typography sx={{ fontSize: '0.65rem', color: '#94a3b8' }}>{t(`riskManagement.portfolioHealth.subMetrics.${mk}`, mk)}</Typography>
                <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#475569', fontFamily: 'monospace' }}>{fmtVal(k, v)}</Typography>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

// ══════════════════════════════════════
function PortfolioHealth() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [holdings, setHoldings] = useState([{ ticker: '', weight: '' }]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [healthScore, setHealthScore] = useState(null);
  const [portfolioActions, setPortfolioActions] = useState(null);
  const [loadingActions, setLoadingActions] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [actionBoard, setActionBoard] = useState(null);
  const lookbackDays = 60;

  // Fetch latest Stock Strength action board for suggestions + warnings
  useEffect(() => {
    let mounted = true;
    stockStrengthAPI.getActionBoard()
      .then(r => { if (mounted) setActionBoard(r.data); })
      .catch(() => { /* silent: feature is optional */ });
    return () => { mounted = false; };
  }, []);

  // Build lookup: ticker -> { bucket, style, score }
  const reviewMap = useMemo(() => {
    const m = {};
    if (!actionBoard) return m;
    (actionBoard.review_holdings || []).forEach(x => {
      m[x.ticker] = { bucket: 'review', style: x.trade_style, score: x.strength_score };
    });
    return m;
  }, [actionBoard]);

  const buyNowList = actionBoard?.buy_now || [];

  const fillFromBuyNow = (n = 8) => {
    const picks = buyNowList.slice(0, n);
    if (picks.length === 0) return;
    const w = (100 / picks.length).toFixed(2);
    setHoldings(picks.map(p => ({ ticker: p.ticker, weight: w })));
  };

  const addHolding = () => setHoldings([...holdings, { ticker: '', weight: '' }]);
  const removeHolding = (i) => { const n = holdings.filter((_, idx) => idx !== i); setHoldings(n.length === 0 ? [{ ticker: '', weight: '' }] : n); };
  const updateHolding = (i, field, val) => { const n = [...holdings]; n[i][field] = val; setHoldings(n); };

  const calculateHealth = async () => {
    setLoading(true); setError(''); setHealthScore(null);
    try {
      const valid = holdings.filter(h => h.ticker && h.weight);
      if (valid.length === 0) { setError(t('riskManagement.portfolioHealth.errors.noHoldings', 'Add at least one holding')); setLoading(false); return; }
      const total = valid.reduce((s, h) => s + parseFloat(h.weight || 0), 0);
      if (total === 0) { setError(t('riskManagement.portfolioHealth.errors.zeroWeight', 'Total weight must be > 0%')); setLoading(false); return; }
      if (total > 100) { setError(t('riskManagement.portfolioHealth.errors.exceedWeight', `Total weight ${total.toFixed(1)}% > 100%`)); setLoading(false); return; }
      if (valid.some(h => parseFloat(h.weight) <= 0)) { setError(t('riskManagement.portfolioHealth.errors.negativeWeight', 'Weights must be > 0%')); setLoading(false); return; }

      const payload = { name: 'My Portfolio', holdings: valid.map(h => ({ ticker: h.ticker.toUpperCase(), weight: parseFloat(h.weight) / 100 })) };
      const resp = await portfolioAPI.calculateHealth(payload);
      if (resp.data.health_score) { setHealthScore(resp.data.health_score); fetchActions(valid); }
      else setError(t('riskManagement.portfolioHealth.errors.calculationFailed', 'Calculation failed'));
    } catch (err) { setError(err.response?.data?.message || 'Calculation failed'); }
    finally { setLoading(false); }
  };

  const fetchActions = async (valid) => {
    setLoadingActions(true);
    try {
      const payload = { name: 'My Portfolio', holdings: valid.map(h => ({ ticker: h.ticker.toUpperCase(), weight: parseFloat(h.weight) / 100 })) };
      const resp = await portfolioAPI.getActions(payload);
      if (resp.data) setPortfolioActions(resp.data);
    } catch (err) { console.error(err); }
    finally { setLoadingActions(false); }
  };

  const hs = healthScore;
  const comps = hs ? [hs.component_a, hs.component_b, hs.component_c, hs.component_d, hs.component_e] : [];
  const hasCritical = comps.some(c => c && c.score < 6);
  const hasWeak = comps.some(c => c && c.score >= 6 && c.score < 10);
  let adjTotal = hs?.total_score || 0;
  if (hasCritical) adjTotal = Math.min(adjTotal, 39);
  else if (hasWeak) adjTotal = Math.min(adjTotal, 59);
  const totalGrade = getGrade(adjTotal);

  const TABS = [
    { key: 'score', label: t('riskManagement.portfolioHealth.healthScore', 'Health Score') },
    { key: 'actions', label: t('riskManagement.portfolioHealth.actions', 'Actions'), disabled: !portfolioActions },
  ];

  return (
    <Box sx={{ p: 2.5, maxWidth: 1400, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 42, height: 42, borderRadius: 2.5, flexShrink: 0,
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: `0 3px 10px ${alpha('#10b981', 0.3)}`,
          }}>
            <HealthAndSafety sx={{ fontSize: 22, color: '#fff' }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: '1.35rem', fontWeight: 800, color: '#1e293b', lineHeight: 1.2 }}>
              {t('riskManagement.portfolioHealth.title', 'Portfolio Health')}
            </Typography>
            <Typography sx={{ fontSize: '0.8rem', color: '#64748b', fontWeight: 500, lineHeight: 1.3, mt: 0.3 }}>
              {t('riskManagement.portfolioHealth.subtitle', 'Analyze your portfolio risk & performance')}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Input Section */}
      <Box sx={{ mb: 2, borderRadius: 2.5, border: '1px solid #e2e8f0', bgcolor: '#fff', p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
          <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#475569', flex: 1 }}>
            {t('riskManagement.portfolioHealth.yourHoldings', 'Holdings')}
          </Typography>
          <Button size="small" startIcon={<Add sx={{ fontSize: 16 }} />} onClick={addHolding}
            sx={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'none', color: '#64748b', '&:hover': { bgcolor: '#f8fafc' } }}>
            {t('riskManagement.portfolioHealth.addHolding', 'Thêm')}
          </Button>
          <Button size="small" variant="contained"
            startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <Assessment sx={{ fontSize: 16 }} />}
            onClick={calculateHealth} disabled={loading}
            sx={{
              fontSize: '0.78rem', fontWeight: 700, textTransform: 'none', borderRadius: 1.5, px: 2,
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              boxShadow: `0 2px 6px ${alpha('#10b981', 0.3)}`,
            }}>
            {loading ? t('riskManagement.portfolioHealth.analyzing', 'Analyzing...') : t('riskManagement.portfolioHealth.calculateScore', 'Phân tích')}
          </Button>
        </Box>

        {error && (
          <Box sx={{ px: 1.5, py: 0.8, mb: 1.5, borderRadius: 1.5, bgcolor: alpha('#ef4444', 0.06), border: `1px solid ${alpha('#ef4444', 0.15)}` }}>
            <Typography sx={{ fontSize: '0.78rem', color: '#ef4444' }}>{error}</Typography>
          </Box>
        )}

        {/* Suggestions from Stock Strength action board */}
        {buyNowList.length > 0 && (
          <Box sx={{ mb: 1.5, px: 1.25, py: 1, borderRadius: 1.5, bgcolor: alpha('#10b981', 0.04), border: `1px dashed ${alpha('#10b981', 0.3)}` }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.8, flexWrap: 'wrap' }}>
              <AutoAwesome sx={{ fontSize: 14, color: '#10b981' }} />
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#065f46' }}>
                Gợi ý từ Stock Strength · Vào lệnh hôm nay ({buyNowList.length})
              </Typography>
              <Box sx={{ flex: 1 }} />
              <Button size="small" onClick={() => fillFromBuyNow(Math.min(8, buyNowList.length))}
                sx={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'none', color: '#10b981',
                  py: 0.2, px: 1, minWidth: 0, '&:hover': { bgcolor: alpha('#10b981', 0.08) } }}>
                Điền top {Math.min(8, buyNowList.length)} (equal-weight)
              </Button>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {buyNowList.slice(0, 12).map(p => (
                <Tooltip key={p.ticker} title={`Score ${p.strength_score?.toFixed(1)} · ${p.trade_style || ''}`} arrow>
                  <Chip
                    label={p.ticker}
                    size="small"
                    onClick={() => {
                      // Append if not already present
                      if (holdings.some(h => h.ticker === p.ticker)) return;
                      const empty = holdings.findIndex(h => !h.ticker);
                      if (empty >= 0) updateHolding(empty, 'ticker', p.ticker);
                      else setHoldings([...holdings, { ticker: p.ticker, weight: '' }]);
                    }}
                    sx={{
                      height: 22, fontSize: '0.68rem', fontWeight: 700, cursor: 'pointer',
                      bgcolor: '#fff', color: '#065f46', border: `1px solid ${alpha('#10b981', 0.35)}`,
                      '&:hover': { bgcolor: alpha('#10b981', 0.1) },
                      '& .MuiChip-label': { px: 0.9 },
                    }}
                  />
                </Tooltip>
              ))}
            </Box>
          </Box>
        )}

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: 0.75 }}>
          {holdings.map((h, i) => {
            const warn = h.ticker ? reviewMap[h.ticker.toUpperCase()] : null;
            return (
            <Box key={i} sx={{
              display: 'flex', alignItems: 'center',
              border: `1px solid ${warn ? alpha('#ef4444', 0.35) : '#e2e8f0'}`,
              borderRadius: 1.2, bgcolor: warn ? alpha('#ef4444', 0.03) : '#fff',
              height: 34, overflow: 'hidden',
              transition: 'border-color .15s',
              '&:focus-within': { borderColor: warn ? '#ef4444' : '#3b82f6' },
            }}>
              <TextField variant="standard" placeholder="Ticker" value={h.ticker}
                onChange={(e) => updateHolding(i, 'ticker', e.target.value.toUpperCase())}
                InputProps={{ disableUnderline: true }}
                sx={{ width: 70, px: 1,
                  '& input': { fontSize: '0.82rem', fontWeight: 700, color: '#1e293b', p: 0, height: 32, letterSpacing: '0.02em' } }} />
              <Box sx={{ width: '1px', height: 18, bgcolor: '#e2e8f0' }} />
              <TextField variant="standard" placeholder="0" type="number" value={h.weight}
                onChange={(e) => updateHolding(i, 'weight', e.target.value)}
                InputProps={{ disableUnderline: true, endAdornment: <Typography sx={{ fontSize: '0.7rem', color: '#94a3b8', mr: 0.5 }}>%</Typography> }}
                sx={{ flex: 1, px: 1,
                  '& input': { fontSize: '0.82rem', fontWeight: 600, color: '#475569', p: 0, height: 32, textAlign: 'right' } }} />
              {warn && (
                <Tooltip arrow title={
                  <Box sx={{ fontSize: '0.72rem' }}>
                    <Box sx={{ fontWeight: 800, mb: 0.3 }}>Cần rà soát</Box>
                    <Box>Style: <b>{warn.style}</b></Box>
                    <Box>Strength: <b>{warn.score?.toFixed(1)}</b></Box>
                  </Box>
                }>
                  <ReportProblem sx={{ fontSize: 15, color: '#ef4444', mr: 0.4 }} />
                </Tooltip>
              )}
              <IconButton onClick={() => removeHolding(i)} disabled={holdings.length === 1} size="small"
                sx={{ color: '#cbd5e1', '&:hover': { color: '#ef4444', bgcolor: 'transparent' }, p: 0.3, mr: 0.2 }}>
                <Delete sx={{ fontSize: 14 }} />
              </IconButton>
            </Box>
            );
          })}
        </Box>

        {/* Quick Stats */}
        {hs && (
          <Box sx={{ display: 'flex', gap: 1, mt: 2, pt: 1.5, borderTop: '1px solid #f1f5f9', justifyContent: 'center', flexWrap: 'wrap' }}>
            {hs.portfolio_return_cumulative != null && (
              <Tile label={t('riskManagement.portfolioHealth.actualReturn', 'Actual Return')}
                sub={`${lookbackDays}d`}
                value={`${(hs.portfolio_return_cumulative * 100).toFixed(2)}%`}
                color={hs.portfolio_return_cumulative >= 0 ? '#10b981' : '#ef4444'} />
            )}
            {hs.portfolio_return != null && (
              <Tile label={t('riskManagement.portfolioHealth.annualizedReturn', 'Annualized')}
                sub={t('riskManagement.portfolioHealth.projected', 'projected')}
                value={`${(hs.portfolio_return * 100).toFixed(2)}%`}
                color={hs.portfolio_return >= 0 ? '#10b981' : '#ef4444'} />
            )}
            {hs.volatility != null && (
              <Tile label={t('riskManagement.portfolioHealth.volatility', 'Volatility')}
                sub={t('riskManagement.portfolioHealth.annual', 'annual')}
                value={`${(hs.volatility * 100).toFixed(2)}%`} color="#475569" />
            )}
            {hs.max_drawdown != null && (
              <Tile label={t('riskManagement.portfolioHealth.maxDrawdown', 'Max Drawdown')}
                sub={`${lookbackDays}d`}
                value={`${(hs.max_drawdown * 100).toFixed(2)}%`} color="#ef4444" />
            )}
          </Box>
        )}
      </Box>

      {/* Empty State */}
      {!hs && !loading && (
        <Box sx={{ py: 6, textAlign: 'center', borderRadius: 2.5, border: '1px solid #e2e8f0', bgcolor: '#fff' }}>
          <Box sx={{ width: 56, height: 56, borderRadius: '50%', bgcolor: '#f8fafc', border: '1.5px dashed #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 1.5 }}>
            <HealthAndSafety sx={{ fontSize: 24, color: '#cbd5e1' }} />
          </Box>
          <Typography sx={{ fontSize: '0.88rem', fontWeight: 600, color: '#94a3b8', mb: 0.3 }}>
            {t('riskManagement.portfolioHealth.analyzeTitle', 'Nhập danh mục để phân tích')}
          </Typography>
          <Typography sx={{ fontSize: '0.75rem', color: '#cbd5e1' }}>
            {t('riskManagement.portfolioHealth.analyzeDesc', 'Thêm cổ phiếu và nhấn Phân tích')}
          </Typography>
        </Box>
      )}

      {/* Loading */}
      {loading && (
        <Box sx={{ borderRadius: 2.5, border: '1px solid #e2e8f0', bgcolor: '#fff', overflow: 'hidden' }}>
          {[...Array(5)].map((_, i) => <Skeleton key={i} height={42} sx={{ mx: 2, mb: 0.5, borderRadius: 1 }} />)}
        </Box>
      )}

      {/* Results */}
      {hs && (
        <>
          {/* Tab bar */}
          <Box sx={{ display: 'flex', gap: 0, borderBottom: '2px solid #e2e8f0', mb: 0 }}>
            {TABS.map((tab, idx) => {
              const active = activeTab === idx;
              const c = idx === 0 ? '#10b981' : '#3b82f6';
              return (
                <Box key={tab.key}
                  onClick={() => !tab.disabled && setActiveTab(idx)}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 0.8, px: 2.5, py: 1.2,
                    cursor: tab.disabled ? 'default' : 'pointer', opacity: tab.disabled ? 0.4 : 1,
                    borderBottom: active ? `2.5px solid ${c}` : '2.5px solid transparent', mb: '-2px',
                    transition: 'all 0.15s', '&:hover': tab.disabled ? {} : { bgcolor: alpha(c, 0.04) },
                  }}>
                  <Typography sx={{ fontSize: '0.85rem', fontWeight: active ? 800 : 600, color: active ? c : '#64748b' }}>
                    {tab.label}
                  </Typography>
                </Box>
              );
            })}
          </Box>

          <Box sx={{ borderRadius: '0 0 10px 10px', border: '1px solid #e2e8f0', borderTop: 'none', bgcolor: '#fff', overflow: 'hidden' }}>
            {/* Tab 0: Health Score */}
            {activeTab === 0 && (
              <Box sx={{ p: 2 }}>
                {/* Summary banner */}
                {(() => {
                  const sev = hasCritical || hs.total_score < 40 ? '#ef4444' : (hasWeak || hs.total_score < 60) ? '#f59e0b' : '#10b981';
                  const msg = hs.total_score >= 60 && !hasWeak
                    ? t('riskManagement.portfolioHealth.summaries.good', '', { score: hs.total_score.toFixed(0) })
                    : hs.total_score >= 40 && !hasCritical
                    ? t('riskManagement.portfolioHealth.summaries.attention', '', { score: hs.total_score.toFixed(0) })
                    : t('riskManagement.portfolioHealth.summaries.urgent', '', { score: hs.total_score.toFixed(0) });
                  return msg ? (
                    <Box sx={{ px: 2, py: 1.2, mb: 2, borderRadius: 1.5, bgcolor: alpha(sev, 0.06), border: `1px solid ${alpha(sev, 0.15)}`, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box component={totalGrade.icon} sx={{ fontSize: 18, color: sev }} />
                      <Typography sx={{ fontSize: '0.8rem', color: '#475569' }}>{msg}</Typography>
                    </Box>
                  ) : null;
                })()}

                {/* Hero Total Score + 5 components */}
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '260px 1fr' }, gap: 1.5, mb: 2 }}>
                  {/* Hero Total */}
                  <Box sx={{
                    borderRadius: 2,
                    background: `linear-gradient(135deg, ${alpha(totalGrade.color, 0.12)} 0%, ${alpha(totalGrade.color, 0.03)} 100%)`,
                    border: `1.5px solid ${alpha(totalGrade.color, 0.35)}`,
                    p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                    position: 'relative', overflow: 'hidden',
                  }}>
                    <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', color: '#64748b', letterSpacing: '0.08em', mb: 0.5 }}>
                      {t('riskManagement.portfolioHealth.totalScore', 'Tổng điểm sức khỏe')}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                      <Typography sx={{ fontSize: '3.2rem', fontWeight: 900, color: totalGrade.color, lineHeight: 1, fontFamily: 'monospace' }}>
                        {hs.total_score?.toFixed(0) || '0'}
                      </Typography>
                      <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: alpha(totalGrade.color, 0.5) }}>/100</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1, px: 1.2, py: 0.3, borderRadius: 1, bgcolor: alpha(totalGrade.color, 0.15) }}>
                      <Box component={totalGrade.icon} sx={{ fontSize: 14, color: totalGrade.color }} />
                      <Typography sx={{ fontSize: '0.75rem', fontWeight: 800, color: totalGrade.color, letterSpacing: '0.03em' }}>
                        {totalGrade.grade} · {totalGrade.label}
                      </Typography>
                    </Box>
                    {/* Mini composition bar */}
                    <Box sx={{ display: 'flex', width: '100%', height: 4, borderRadius: 2, overflow: 'hidden', mt: 1.5, bgcolor: '#f1f5f9' }}>
                      {[hs.component_a, hs.component_b, hs.component_c, hs.component_d, hs.component_e].map((c, i) => {
                        const colors = ['#f59e0b', '#10b981', '#3b82f6', '#8b5cf6', '#06b6d4'];
                        const pct = ((c?.score || 0) / 100) * 100;
                        return <Box key={i} sx={{ flex: 1, bgcolor: alpha(colors[i], 0.15), position: 'relative' }}>
                          <Box sx={{ position: 'absolute', inset: 0, width: `${pct}%`, bgcolor: colors[i] }} />
                        </Box>;
                      })}
                    </Box>
                  </Box>

                  {/* 5 components grid */}
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 1, '@media (max-width: 1100px)': { gridTemplateColumns: 'repeat(3, 1fr)' }, '@media (max-width: 700px)': { gridTemplateColumns: 'repeat(2, 1fr)' } }}>
                    {[
                      { c: hs.component_a, label: 'A. Performance', key: 'component_a' },
                      { c: hs.component_b, label: 'B. Trend', key: 'component_b' },
                      { c: hs.component_c, label: 'C. Liquidity', key: 'component_c' },
                      { c: hs.component_d, label: 'D. Concentration', key: 'component_d' },
                      { c: hs.component_e, label: 'E. Stress', key: 'component_e' },
                    ].map(({ c: comp, label, key }) => (
                      <ComponentCard key={key} component={comp} label={label} componentKey={key} lang={lang} t={t} healthScore={hs} />
                    ))}
                  </Box>
                </Box>

                {/* Weak Points & Action Items */}
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.5 }}>
                  {/* Weak Points */}
                  {hs.weak_points && ((typeof hs.weak_points === 'object' ? hs.weak_points[lang] : hs.weak_points) || []).length > 0 && (
                    <Box sx={{ borderRadius: 2, border: `1px solid ${alpha('#f59e0b', 0.2)}`, p: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                        <WarningAmber sx={{ fontSize: 16, color: '#f59e0b' }} />
                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#f59e0b' }}>
                          {t('riskManagement.portfolioHealth.weakPoints', 'Weak Points')}
                        </Typography>
                      </Box>
                      {(typeof hs.weak_points === 'object' ? hs.weak_points[lang] : hs.weak_points).map((p, i) => (
                        <Typography key={i} sx={{ fontSize: '0.76rem', color: '#64748b', lineHeight: 1.5, pl: 1, borderLeft: `2px solid ${alpha('#f59e0b', 0.2)}`, mb: 0.5 }}>
                          {p}
                        </Typography>
                      ))}
                    </Box>
                  )}
                  {/* Action Items */}
                  {hs.action_items && ((typeof hs.action_items === 'object' ? hs.action_items[lang] : hs.action_items) || []).length > 0 && (
                    <Box sx={{ borderRadius: 2, border: `1px solid ${alpha('#3b82f6', 0.2)}`, p: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                        <Info sx={{ fontSize: 16, color: '#3b82f6' }} />
                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#3b82f6' }}>
                          {t('riskManagement.portfolioHealth.actionItems', 'Actions')}
                        </Typography>
                      </Box>
                      {(typeof hs.action_items === 'object' ? hs.action_items[lang] : hs.action_items).map((item, i) => {
                        const pri = hs.action_priorities?.[i];
                        return (
                          <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.8, mb: 0.5 }}>
                            <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', fontFamily: 'monospace', mt: 0.1 }}>{i + 1}.</Typography>
                            <Typography sx={{ fontSize: '0.76rem', color: '#64748b', lineHeight: 1.5, flex: 1 }}>{item}</Typography>
                            {pri === 'high' && (
                              <Box sx={{ display: 'inline-flex', px: 0.5, py: 0.1, borderRadius: 0.5, bgcolor: alpha('#ef4444', 0.1) }}>
                                <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#ef4444' }}>HIGH</Typography>
                              </Box>
                            )}
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </Box>
              </Box>
            )}

            {/* Tab 1: Actions */}
            {activeTab === 1 && portfolioActions && (
              <Box sx={{ p: 2 }}>
                {loadingActions ? (
                  <Box sx={{ p: 3 }}>
                    {[...Array(4)].map((_, i) => <Skeleton key={i} height={38} sx={{ mb: 0.5, borderRadius: 1 }} />)}
                  </Box>
                ) : (
                  <>
                    {/* Summary */}
                    {(() => {
                      const counts = portfolioActions.summary?.action_counts || {};
                      const keep = counts.KEEP || 0, trim = counts.TRIM || 0, exit = counts.EXIT || 0;
                      const curExp = portfolioActions.summary?.current_exposure || 0;
                      const recExp = portfolioActions.summary?.recommended_exposure || 0;
                      const expDelta = recExp - curExp;
                      const highPri = portfolioActions.summary?.high_priority_actions || 0;

                      return (
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1.7fr 1fr' }, gap: 1.5 }}>
                          {/* ═══ LEFT: Position Actions ═══ */}
                          <Box>
                            {/* Compact summary strip on top of table */}
                            <Box sx={{
                              display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap',
                              px: 1.5, py: 1, mb: 1, borderRadius: 1.5,
                              border: `1px solid ${highPri > 0 ? alpha('#ef4444', 0.25) : '#e2e8f0'}`,
                              bgcolor: highPri > 0 ? alpha('#ef4444', 0.03) : '#fafbfc',
                            }}>
                              {/* Action mix */}
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                                <Typography sx={{ fontSize: '0.6rem', color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Phân bổ</Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                                  <Tooltip title="Giữ" arrow><Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                                    <CheckCircle sx={{ fontSize: 13, color: '#10b981' }} />
                                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 800, color: '#10b981', fontFamily: 'monospace' }}>{keep}</Typography>
                                  </Box></Tooltip>
                                  <Tooltip title="Giảm" arrow><Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                                    <RemoveCircleOutline sx={{ fontSize: 13, color: '#f59e0b' }} />
                                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 800, color: '#f59e0b', fontFamily: 'monospace' }}>{trim}</Typography>
                                  </Box></Tooltip>
                                  <Tooltip title="Thoát" arrow><Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                                    <HighlightOff sx={{ fontSize: 13, color: '#ef4444' }} />
                                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 800, color: '#ef4444', fontFamily: 'monospace' }}>{exit}</Typography>
                                  </Box></Tooltip>
                                </Box>
                                <Box sx={{ width: 70, display: 'flex', height: 4, borderRadius: 2, overflow: 'hidden', bgcolor: '#f1f5f9' }}>
                                  {keep > 0 && <Box sx={{ flex: keep, bgcolor: '#10b981' }} />}
                                  {trim > 0 && <Box sx={{ flex: trim, bgcolor: '#f59e0b' }} />}
                                  {exit > 0 && <Box sx={{ flex: exit, bgcolor: '#ef4444' }} />}
                                </Box>
                              </Box>

                              <Box sx={{ width: '1px', height: 22, bgcolor: '#e2e8f0' }} />

                              {/* Exposure */}
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                                <Typography sx={{ fontSize: '0.6rem', color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Tỷ trọng</Typography>
                                <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#64748b', fontFamily: 'monospace' }}>{curExp.toFixed(0)}%</Typography>
                                <Typography sx={{ fontSize: '0.7rem', color: '#cbd5e1' }}>→</Typography>
                                <Typography sx={{ fontSize: '0.9rem', fontWeight: 900, color: expDelta < 0 ? '#ef4444' : expDelta > 0 ? '#10b981' : '#475569', fontFamily: 'monospace' }}>{recExp.toFixed(0)}%</Typography>
                                {Math.abs(expDelta) >= 0.1 && (
                                  <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: expDelta < 0 ? '#ef4444' : '#10b981' }}>
                                    {expDelta > 0 ? '+' : ''}{expDelta.toFixed(1)}pp
                                  </Typography>
                                )}
                              </Box>

                              <Box sx={{ flex: 1 }} />

                              {/* High priority badge */}
                              {highPri > 0 ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, px: 1, py: 0.3, borderRadius: 1, bgcolor: '#ef4444' }}>
                                  <ReportProblem sx={{ fontSize: 13, color: '#fff' }} />
                                  <Typography sx={{ fontSize: '0.7rem', fontWeight: 800, color: '#fff' }}>{highPri} cần xử lý</Typography>
                                </Box>
                              ) : (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, px: 1, py: 0.3, borderRadius: 1, bgcolor: alpha('#10b981', 0.12) }}>
                                  <CheckCircle sx={{ fontSize: 13, color: '#10b981' }} />
                                  <Typography sx={{ fontSize: '0.7rem', fontWeight: 800, color: '#10b981' }}>An toàn</Typography>
                                </Box>
                              )}
                            </Box>

                            {/* Position Actions Table */}
                            <TableContainer sx={{ maxHeight: 460, border: '1px solid #e2e8f0', borderRadius: 1.5 }}>
                              <Table size="small" stickyHeader>
                                <TableHead>
                                  <TableRow>
                                    <TableCell sx={{ ...thSx, width: 60 }}>{t('riskManagement.portfolioHealth.actionsTable.ticker', 'Mã')}</TableCell>
                                    <TableCell sx={{ ...thSx, width: 78 }}>{t('riskManagement.portfolioHealth.actionsTable.action', 'Hành động')}</TableCell>
                                    <TableCell align="right" sx={{ ...thSx, width: 120 }}>Hiện tại → Mục tiêu</TableCell>
                                    <TableCell sx={{ ...thSx, width: 70 }}>{t('riskManagement.portfolioHealth.actionsTable.priority', 'Ưu tiên')}</TableCell>
                                    <TableCell sx={thSx}>{t('riskManagement.portfolioHealth.actionsTable.reason', 'Lý do')}</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {portfolioActions.position_actions?.map((pos, idx) => {
                                    const actionClr = { KEEP: '#10b981', TRIM: '#f59e0b', EXIT: '#ef4444', WATCH: '#3b82f6' }[pos.action] || '#64748b';
                                    const ActionIcon = { KEEP: CheckCircle, TRIM: RemoveCircleOutline, EXIT: HighlightOff, WATCH: Visibility }[pos.action] || Info;
                                    const priClr = pos.priority === 'high' ? '#ef4444' : pos.priority === 'medium' ? '#f59e0b' : '#94a3b8';
                                    const deltaPp = (pos.recommended_weight ?? 0) - (pos.current_weight ?? 0);
                                    const deltaClr = deltaPp < 0 ? '#ef4444' : deltaPp > 0 ? '#10b981' : '#64748b';
                                    const reasonText = typeof pos.reasons === 'object' ? pos.reasons[lang] || pos.reasons.en : pos.reasons;
                                    return (
                                      <TableRow key={idx} sx={{ '&:hover': { bgcolor: '#fafbfc' } }}>
                                        <TableCell sx={tdSx}><Typography sx={{ fontWeight: 800, fontSize: '0.82rem', color: '#1e293b', letterSpacing: '0.02em' }}>{pos.ticker}</Typography></TableCell>
                                        <TableCell sx={tdSx}>
                                          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4, px: 0.6, py: 0.2, borderRadius: 1, bgcolor: alpha(actionClr, 0.1) }}>
                                            <ActionIcon sx={{ fontSize: 12, color: actionClr }} />
                                            <Typography sx={{ fontSize: '0.66rem', fontWeight: 800, color: actionClr, letterSpacing: '0.03em' }}>{pos.action}</Typography>
                                          </Box>
                                        </TableCell>
                                        <TableCell align="right" sx={tdSx}>
                                          <Box sx={{ display: 'inline-flex', alignItems: 'baseline', gap: 0.3 }}>
                                            <Typography component="span" sx={{ fontSize: '0.72rem', color: '#94a3b8', fontFamily: 'monospace' }}>{pos.current_weight?.toFixed(1)}%</Typography>
                                            <Typography component="span" sx={{ fontSize: '0.68rem', color: '#cbd5e1' }}>→</Typography>
                                            <Typography component="span" sx={{ fontSize: '0.8rem', fontWeight: 800, color: deltaClr, fontFamily: 'monospace' }}>{pos.recommended_weight?.toFixed(1)}%</Typography>
                                            {Math.abs(deltaPp) >= 0.1 && (
                                              <Typography component="span" sx={{ fontSize: '0.6rem', color: deltaClr, fontWeight: 700, ml: 0.3 }}>
                                                ({deltaPp > 0 ? '+' : ''}{deltaPp.toFixed(1)})
                                              </Typography>
                                            )}
                                          </Box>
                                        </TableCell>
                                        <TableCell sx={tdSx}>
                                          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3 }}>
                                            <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: priClr }} />
                                            <Typography sx={{ fontSize: '0.62rem', fontWeight: 800, color: priClr, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{pos.priority}</Typography>
                                          </Box>
                                        </TableCell>
                                        <TableCell sx={tdSx}>
                                          <Typography sx={{ fontSize: '0.74rem', color: '#475569', lineHeight: 1.5 }}>
                                            {renderReason(reasonText)}
                                          </Typography>
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </Box>

                          {/* ═══ RIGHT: Portfolio-Level Actions ═══ */}
                          {portfolioActions.portfolio_actions?.length > 0 ? (
                            <Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, mb: 1, px: 0.2 }}>
                                <Box sx={{ width: 3, height: 14, borderRadius: 1, bgcolor: '#3b82f6' }} />
                                <Typography sx={{ fontSize: '0.78rem', fontWeight: 800, color: '#1e293b', letterSpacing: '0.02em' }}>
                                  {t('riskManagement.portfolioHealth.portfolioActions', 'Hành động danh mục')}
                                </Typography>
                                <Box sx={{ display: 'inline-flex', px: 0.6, py: 0.1, borderRadius: 0.6, bgcolor: alpha('#3b82f6', 0.1) }}>
                                  <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#3b82f6', fontFamily: 'monospace' }}>{portfolioActions.portfolio_actions.length}</Typography>
                                </Box>
                              </Box>
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
                                {portfolioActions.portfolio_actions
                                  .slice()
                                  .sort((a, b) => {
                                    const order = { high: 0, medium: 1, low: 2 };
                                    return (order[a.priority] ?? 3) - (order[b.priority] ?? 3);
                                  })
                                  .map((a, i) => {
                                    const priClr = a.priority === 'high' ? '#ef4444' : a.priority === 'medium' ? '#f59e0b' : '#3b82f6';
                                    const PriIcon = a.priority === 'high' ? ReportProblem : a.priority === 'medium' ? WarningAmber : Info;
                                    const reasonText = typeof a.reason === 'object' ? a.reason[lang] || a.reason.en : a.reason;
                                    return (
                                      <Box key={i} sx={{
                                        display: 'flex', gap: 1, p: 1.1, borderRadius: 1.5,
                                        border: `1px solid ${alpha(priClr, 0.2)}`, bgcolor: alpha(priClr, 0.02),
                                        transition: 'all .15s',
                                        '&:hover': { borderColor: alpha(priClr, 0.4), boxShadow: `0 2px 8px ${alpha(priClr, 0.1)}` },
                                      }}>
                                        <Box sx={{
                                          width: 28, height: 28, borderRadius: 1.2, flexShrink: 0,
                                          bgcolor: alpha(priClr, 0.12), display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        }}>
                                          <PriIcon sx={{ fontSize: 16, color: priClr }} />
                                        </Box>
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.2 }}>
                                            <Typography sx={{ fontSize: '0.78rem', fontWeight: 800, color: '#1e293b' }}>
                                              {t(`riskManagement.portfolioHealth.portfolioActionTypes.${a.type}`, a.type.replace(/_/g, ' '))}
                                            </Typography>
                                            <Box sx={{ display: 'inline-flex', px: 0.5, py: 0.05, borderRadius: 0.4, bgcolor: priClr }}>
                                              <Typography sx={{ fontSize: '0.56rem', fontWeight: 800, color: '#fff', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{a.priority}</Typography>
                                            </Box>
                                          </Box>
                                          <Typography sx={{ fontSize: '0.72rem', color: '#64748b', lineHeight: 1.45 }}>
                                            {renderReason(reasonText)}
                                          </Typography>
                                        </Box>
                                      </Box>
                                    );
                                  })}
                              </Box>
                            </Box>
                          ) : (
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 4, borderRadius: 1.5, border: '1px dashed #e2e8f0', bgcolor: '#fafbfc' }}>
                              <CheckCircle sx={{ fontSize: 28, color: '#cbd5e1', mb: 0.5 }} />
                              <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8', fontWeight: 600 }}>Không có hành động danh mục</Typography>
                            </Box>
                          )}
                        </Box>
                      );
                    })()}
                  </>
                )}
              </Box>
            )}
          </Box>
        </>
      )}
    </Box>
  );
}

export default PortfolioHealth;
